import { useEffect, useRef, useState } from 'react';
import { howItWorks } from '../../content';
import { HandoverExplainer, OwnerScanExplainer, TagExplainer } from '../../explainers';
import { cx } from '../../ui';
import { reduced, useVisible } from './kit';

// Ramp-style tabbed "how it works": progress bar fills on the active tab, then auto-advances.
// Clicking a tab takes control (auto-advance stops). Paused off-screen.
const DURATION = 8000;
const VISUALS = [TagExplainer, HandoverExplainer, OwnerScanExplainer];

export function HowTabs() {
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(() => !reduced());
  const [progress, setProgress] = useState(0);
  const [ref, visible] = useVisible<HTMLDivElement>(0.35);
  const start = useRef(0);

  useEffect(() => {
    if (!auto || !visible) return;
    let raf = 0;
    start.current = performance.now() - progress * DURATION;
    const tick = (now: number) => {
      const t = (now - start.current) / DURATION;
      if (t >= 1) {
        setProgress(0);
        setActive((a) => (a + 1) % VISUALS.length);
        return;
      }
      setProgress(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, auto, visible]);

  const pick = (i: number) => {
    setAuto(false);
    setProgress(1);
    setActive(i);
  };

  const Visual = VISUALS[active];

  return (
    <div ref={ref} className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-16">
      <div role="tablist" aria-label="How it works" aria-orientation="vertical" className="self-center">
        {howItWorks.steps.map((s, i) => {
          const on = i === active;
          return (
            <button
              key={s.k}
              id={`vb-tab-${i}`}
              role="tab"
              type="button"
              aria-selected={on}
              aria-controls="vb-tabpanel"
              className="vb-tab"
              onClick={() => pick(i)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                  e.preventDefault();
                  pick((i + 1) % 3);
                  document.getElementById(`vb-tab-${(i + 1) % 3}`)?.focus();
                }
                if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  pick((i + 2) % 3);
                  document.getElementById(`vb-tab-${(i + 2) % 3}`)?.focus();
                }
              }}
            >
              <span className="vb-tab-bar" style={{ width: '100%', transform: `scaleX(${on ? (auto ? progress : 1) : 0})` }} aria-hidden="true" />
              <span className="flex items-baseline gap-4">
                <span className={cx('vb-mono text-[13px]', on ? 'text-bm-emerald' : 'text-[#A3A1C2]')}>{s.n}</span>
                <span className={cx('text-[22px] font-bold tracking-[-0.02em] transition-colors duration-200 md:text-[24px]', on ? 'text-bm-indigo' : 'text-[#9E9CBD] hover:text-bm-indigo')}>
                  {s.k}
                </span>
              </span>
              <span className="vb-tab-body">
                <span className="block">
                  <span className="block pl-[38px] pt-2 text-[16px] font-semibold text-bm-ink">{s.t}</span>
                  <span className="block pl-[38px] pt-1 text-[16px] leading-[1.6] text-bm-muted">{s.d}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div id="vb-tabpanel" role="tabpanel" aria-labelledby={`vb-tab-${active}`} className="min-w-0">
        <Visual key={active} tone="light" autoPlay />
      </div>
    </div>
  );
}
